import React, { useCallback, useState } from 'react';

import { LoadingButton } from '@atlaskit/button';
import { FormFooter } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import { useCurrentUser } from 'src/services/pagerduty/currentUser';
import { useIncident } from 'src/services/pagerduty/incident';
import { useProjectIssueTypes } from 'src/services/pagerduty/projectIssueTypes';

import { identifyFromUser, trackEvent } from 'src/lib/analytics';
import integration from 'src/lib/api/pagerduty-ui/integration';
import { getQueryParams } from 'src/lib/history';

import CenteredSpinner from 'src/views/components/CenteredSpinner';
import ErrorMessage from 'src/views/components/ErrorMessage';

import CreateIssueFormStateless from './CreateIssueFormStateless';
import formatDescription from './formatDescription';
import { hasErrors, notEmpty } from './validation';

const SuccessMessage = ({ issue: { html_url: linkRef, key: linkText } }) => {
  const issueLink = (
    <a href={linkRef} target="_blank" rel="noopener noreferrer">{linkText}</a>
  );

  return (
    <SectionMessage appearance="confirmation">
      You created a Jira Issue for this incident [{issueLink}]
    </SectionMessage>
  );
};

const CreateIssueForm = ({ incident, user }) => {
  const [form, setForm] = useState({
    values: {
      summary: incident.title || '',
      description: (
        (incident.first_trigger_log_entry.channel && formatDescription({ url: incident.html_url, details: incident.first_trigger_log_entry.channel.details })) || ''
      ),
      projectIssueType: {
        project: '',
        issueType: '',
      },
    },
    touched: {},
  });
  const [{ isSubmitting, submitError, submitResponse, isSubmitSuccessful }, setSubmittingStatus] = useState({});

  const { values, touched } = form;
  const { accounts_mapping_id: accountsMappingId } = getQueryParams();

  const errors = {
    summary: notEmpty(values.summary, 'Can not be empty'),
    projectIssueType: {
      project: notEmpty(values.projectIssueType.project, 'Can not be empty'),
      issueType: notEmpty(values.projectIssueType.issueType, 'Can not be empty'),
    },
  };

  const isSubmitDisabled = hasErrors(errors);

  const onSubmit = (event) => {
    event.preventDefault();

    trackEvent('incident_create_jira_issue', { label: 'create' });

    const issueData = {
      fields: {
        summary: values.summary,
        description: values.description,
        project: {
          key: values.projectIssueType.project,
        },
        issuetype: {
          id: values.projectIssueType.issueType
        },
      }
    };

    setSubmittingStatus({ isSubmitting: true, submitError: undefined, submitResponse: undefined, isSubmitSuccessful: undefined });

    integration.createIssue(accountsMappingId, incident, issueData, user)
      .then(submitResponse => setSubmittingStatus({ isSubmitting: false, submitResponse, isSubmitSuccessful: true }))
      .catch(submitError => setSubmittingStatus({ isSubmitting: false, submitError, isSubmitSuccessful: false }));
  };

  const onChange = useCallback(
    ({ target: { name, value } }) => {
      setForm((form) => ({
        ...form,
        values: {
          ...form.values,
          [name]: value,
        },
      }));
    },
    [],
  );

  const onBlur = useCallback(
    ({ target: { name, value } }) => {
      setForm((form) => ({
        ...form,
        touched: {
          ...form.touched,
          [name]: value ? value : true,
        },
      }));
    },
    [],
  );

  return (
    <form onSubmit={onSubmit}>
      <CreateIssueFormStateless
        values={values}
        touched={touched}
        errors={errors}
        onChange={onChange}
        onBlur={onBlur}
      />

      {submitResponse && submitResponse.issue && <SuccessMessage issue={submitResponse.issue} />}
      {submitError && <ErrorMessage error={['Failed to create issue', submitError]} />}

      <FormFooter>
        <LoadingButton type="submit" appearance="primary" isLoading={isSubmitting} isDisabled={isSubmitDisabled || isSubmitSuccessful}>Create</LoadingButton>
      </FormFooter>
    </form>
  );
};

const CreateIssueFormWrapper = () => {
  const { incident } = useIncident();
  const { projectIssueTypes, loading: projectIssueTypesLoading, error: projectIssueTypesError } = useProjectIssueTypes();
  const { user, userLoading, userError } = useCurrentUser();

  if (projectIssueTypesLoading || userLoading) {
    return <CenteredSpinner size="large" />;
  }
  if (incident && projectIssueTypes && user) {
    identifyFromUser(user);
    return <CreateIssueForm incident={incident} user={user} />;
  }

  return <ErrorMessage error={['Failed to load', projectIssueTypesError || userError || 'Unknown error']}/>;
};

export default CreateIssueFormWrapper;
